import { blue, cyan, geekblue, gold, green, magenta, orange, purple, red, volcano } from '@ant-design/colors';
import { UserOutlined } from '@ant-design/icons';
import { css, cx } from '@emotion/css';
import { Avatar, theme } from 'antd';
import type React from 'react';

// Ten of antd's thirteen preset palettes. Left out: pink, yellow and lime — at avatar size they
// read as duplicates of magenta, gold and green. The order is load-bearing: it decides which user
// gets which colour, so appending is safe but reordering repaints everyone.
const PALETTES = [red, volcano, orange, gold, green, cyan, blue, geekblue, purple, magenta];

// These arrays are 0-based while the preset names are 1-based, so index 1 is preset-2. Preset-2 as
// the ground, preset-9 as the initials
const TINT_GROUND = 1;
const TINT_INITIALS = 8;

// djb2 over the OIDC subject, which never changes for a user — same colour on every device
// and in both UIs, and unaffected by a rename.
const paletteFor = (userId: string) => {
  let hash = 5381;
  for (const char of userId) {
    hash = ((hash << 5) + hash + (char.codePointAt(0) ?? 0)) >>> 0;
  }
  return PALETTES[hash % PALETTES.length];
};

const firstChar = (value: string) => [...value][0] ?? '';

/** Initials for the avatar: given + family name, else the first two letters of the username. */
export const userInitials = (givenName?: string, familyName?: string, username?: string): string => {
  if (givenName && familyName) {
    return (firstChar(givenName) + firstChar(familyName)).toUpperCase();
  }
  const fallback = givenName || familyName || username || '';
  return [...fallback.replace(/[^\p{L}\p{N}]/gu, '')].slice(0, 2).join('').toUpperCase();
};

export interface UserAvatarProps {
  /** Stable id the colour is derived from — the OIDC `sub`. */
  userId?: string;
  givenName?: string;
  familyName?: string;
  /** Fallback for the initials when no name is set on the account. */
  username?: string;
  className?: string;
}

export const UserAvatar: React.FC<UserAvatarProps> = ({ userId, givenName, familyName, username, className }) => {
  const { token } = theme.useToken();
  const initials = userInitials(givenName, familyName, username);

  const initialsStyle = css`
    font-size: ${token.fontSize}px;
    font-weight: 500;
    letter-spacing: 0.02em;
  `;

  if (!userId || !initials) {
    return <Avatar className={className} icon={<UserOutlined />} />;
  }

  const palette = paletteFor(userId);
  const label = givenName && familyName ? `${givenName} ${familyName}` : username;

  return (
    <Avatar
      className={cx(initialsStyle, className)}
      style={{ backgroundColor: palette[TINT_GROUND], color: palette[TINT_INITIALS] }}
      aria-label={label}
    >
      {initials}
    </Avatar>
  );
};
